import React, { useMemo, useState } from "react";
import { GlassCard } from "../gc/ui";
import { NOTE_LINK_TYPES, DEFAULT_MODULES, flattenEntities, newStudioNote, IDEA_SUBTYPES } from "../../lib/gamecraft";
import { Plus, Trash, Link as LinkIcon } from "@phosphor-icons/react";
import ModuleShell, { EmptyCraft } from "../gc/ModuleShell";
import { studioHealth } from "../../lib/studioHealth";

const CATS = [
  { id: "idea", label: "Idea" },
  { id: "comment", label: "Comentario" },
  { id: "todo", label: "Por hacer" },
  { id: "playtest", label: "Playtest" },
  { id: "lore", label: "Lore" },
];

export default function NotesPanel({ project, onChange, filterModule, onJump, focus }) {
  const notes = project.notes || [];
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [live, setLive] = useState(false);
  const modules = project.idea_modules?.length ? project.idea_modules : DEFAULT_MODULES;

  const filtered = useMemo(() => {
    return notes.filter((n) => {
      if (cat !== "all" && n.category !== cat) return false;
      if (filterModule && !(n.links || []).some((l) => l.type === "module" && l.id === filterModule)) return false;
      if (live && focus && !(n.links || []).some((l) => l.id === focus.entityId || l.id === focus.spriteId || l.id === focus.fileId)) return false;
      const blob = `${n.title} ${n.body} ${(n.links || []).map((l) => l.label).join(" ")}`.toLowerCase();
      return !q || blob.includes(q.toLowerCase());
    });
  }, [notes, q, cat, filterModule, live, focus]);

  const save = (list) => onChange({ notes: list });

  const add = () => {
    const note = newStudioNote({
      title: "Nueva nota",
      links: filterModule ? [{ type: "module", id: filterModule, label: modules.find((m) => m.id === filterModule)?.name || filterModule }] : [],
    });
    save([note, ...notes]);
    setEditing(note.id);
  };

  const update = (id, patch) => {
    save(notes.map((n) => (n.id === id ? { ...n, ...patch, updated_at: new Date().toISOString() } : n)));
  };

  const current = notes.find((n) => n.id === editing);
  const score = studioHealth(project).modules.find((m) => m.id === "notes")?.score ?? 0;

  return (
    <ModuleShell
      title="Notas del estudio"
      subtitle="Comentarios ligados a módulo, entidad, sprite, audio o archivo."
      beginner="Una nota de playtest tras cada Run. Lía al héroe o al arma que falló."
      expert="Ctrl+K busca notas. Alt+0 abre este panel."
      score={score}
      actions={(
        <button type="button" className="gc-btn gc-btn-primary h-10 px-4" onClick={add}>
          <Plus size={16} /> Nueva
        </button>
      )}
    >
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="flex gap-2 overflow-x-auto gc-hscroll pb-1">
        <button type="button" className={"gc-chip shrink-0 " + (cat === "all" && !live ? "gc-chip-on" : "")} onClick={() => { setCat("all"); setLive(false); }}>Todas</button>
        <button type="button" className={"gc-chip shrink-0 " + (live ? "gc-chip-on" : "")} onClick={() => setLive((v) => !v)}>Del elenco</button>
        {CATS.map((c) => (
          <button key={c.id} type="button" className={"gc-chip shrink-0 " + (cat === c.id ? "gc-chip-on" : "")} onClick={() => setCat(c.id)}>{c.label}</button>
        ))}
      </div>
      <input className="gc-input h-11 w-full px-3" placeholder="Buscar notas…" value={q} onChange={(e) => setQ(e.target.value)} />

      <div className="space-y-2">
        {filtered.map((n) => (
          <GlassCard key={n.id} hover className="p-4" onClick={() => setEditing(n.id)}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{n.title || "Sin título"}</div>
                <div className="text-xs mt-1 line-clamp-2" style={{ color: "var(--gc-muted)" }}>{n.body || "—"}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="gc-chip text-[10px]">{CATS.find((c) => c.id === n.category)?.label || n.category}</span>
                  {(n.links || []).map((l, i) => (
                    <span key={i} className="gc-chip text-[10px]"><LinkIcon size={10} /> {l.label || l.id}</span>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>
        ))}
        {!filtered.length && (
          <EmptyCraft
            title="Aún no hay notas"
            body="Guarda un comentario ligado a un personaje, arma o escena."
            cta="Nueva nota"
            onCta={add}
            tips={["Desde Ideas: Guardar como nota del estudio", "Categoría Playtest tras cada Run", "Los vínculos saltan al módulo"]}
          />
        )}
      </div>

      {current && (
        <NoteEditor
          note={current}
          project={project}
          modules={modules}
          onChange={(patch) => update(current.id, patch)}
          onClose={() => setEditing(null)}
          onDelete={() => { save(notes.filter((n) => n.id !== current.id)); setEditing(null); }}
          onJump={onJump}
        />
      )}
    </div>
    </ModuleShell>
  );
}

function NoteEditor({ note, project, modules, onChange, onClose, onDelete, onJump }) {
  const ents = flattenEntities(project);
  const [linkType, setLinkType] = useState("module");
  const [linkId, setLinkId] = useState("");

  const options = useMemo(() => {
    if (linkType === "module") return modules.map((m) => ({ id: m.id, label: m.name }));
    if (linkType === "entity") return ents.map((e) => ({ id: e.id, label: `${e.name} (${e.kind})` }));
    if (linkType === "class") {
      const all = Object.values(IDEA_SUBTYPES || {}).flat();
      return all.map((s) => ({ id: s.id, label: s.label }));
    }
    if (linkType === "sprite") return (project.sprites || []).map((s) => ({ id: s.id, label: s.name }));
    if (linkType === "audio") return [...(project.soundboard || []), ...(project.samples || [])].map((s) => ({ id: s.id, label: s.name }));
    if (linkType === "file") return (project.files || []).map((f) => ({ id: f.id, label: f.path }));
    if (linkType === "story") return (project.story_items || []).map((s) => ({ id: s.id, label: s.name }));
    if (linkType === "engine") return [{ id: "playtest", label: "Playtest / motor" }];
    return [];
  }, [linkType, modules, ents, project]);

  const addLink = () => {
    const opt = options.find((o) => o.id === linkId) || options[0];
    if (!opt) return;
    const links = [...(note.links || []), { type: linkType, id: opt.id, label: opt.label }];
    onChange({ links });
    setLinkId("");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-3">
      <div className="gc-card w-full max-w-lg max-h-[92vh] overflow-auto p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Editar nota</h3>
          <button type="button" className="gc-btn gc-btn-ghost h-10 px-3" onClick={onClose}>Cerrar</button>
        </div>
        <input className="gc-input h-11 w-full px-3" value={note.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Título" />
        <select className="gc-input h-11 px-3 w-full" value={note.category || "idea"} onChange={(e) => onChange({ category: e.target.value })}>
          {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <textarea className="gc-input w-full p-3 text-sm min-h-[140px]" value={note.body || ""} onChange={(e) => onChange({ body: e.target.value })} placeholder="Comentario, idea, recordatorio…" />
        <div className="text-xs font-semibold" style={{ color: "var(--gc-muted)" }}>Ligado a</div>
        <div className="flex flex-wrap gap-1">
          {(note.links || []).map((l, i) => (
            <button
              key={i}
              type="button"
              className="gc-chip"
              onClick={() => onChange({ links: (note.links || []).filter((_, j) => j !== i) })}
              title="Quitar"
            >
              {NOTE_LINK_TYPES.find((t) => t.id === l.type)?.label}: {l.label || l.id} ×
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select className="gc-input h-11 px-3 flex-1" value={linkType} onChange={(e) => { setLinkType(e.target.value); setLinkId(""); }}>
            {NOTE_LINK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <select className="gc-input h-11 px-3 flex-1" value={linkId} onChange={(e) => setLinkId(e.target.value)}>
            <option value="">Elegir…</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button type="button" className="gc-btn gc-btn-ghost h-11 px-4" onClick={addLink}>Añadir vínculo</button>
        </div>
        <div className="flex gap-2 pt-2">
          {onJump && (
            <button type="button" className="gc-btn gc-btn-ghost h-11 px-4" onClick={() => { onJump("ideas"); onClose(); }}>Ir a Ideas</button>
          )}
          <button type="button" className="gc-btn gc-btn-ghost h-11 px-4 text-red-400 ml-auto" onClick={onDelete}><Trash size={16} /> Borrar</button>
        </div>
      </div>
    </div>
  );
}
